import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, IntlProvider } from 'react-intl';
import { Form, Input, Radio, Select, notification } from 'antd';
import { AppState } from '../../../../store';
import { updateUserProfile } from '../../../../api/users';
import { updateAuthUserProps } from '../../../../reducers/global/types';
import { VButton } from '../../../common/components/button/button.styles';
import VctorsLogo from '../../../../assets/images/vctors-logo.svg';
import AppLocale from '../../../../config/translation';
import styles from './styles.module.scss';

const { Option } = Select;

// Local-number rules (after the country code, no leading trunk "0"):
// Egypt mobiles start 010/011/012/015 -> 1[0125] + 8 digits = 10 digits.
// Saudi mobiles start 05x -> 5 + 8 digits = 9 digits.
const MOBILE_RULES: Record<string, { pattern: RegExp; length: number; example: string }> = {
  '+20': { pattern: /^1[0125][0-9]{8}$/, length: 10, example: '1012345678' },
  '+966': { pattern: /^5[0-9]{8}$/, length: 9, example: '512345678' },
};

// AuthRoute renders this gate in place of `props.children`, so it sits
// outside AppProvider's <IntlProvider> (see src/App.tsx) — provide our own.
const CompleteProfile: React.FC = () => {
  const locale = useSelector((state: any) => state.LanguageSwitcher.language.locale) as keyof typeof AppLocale;
  const currentAppLocale = AppLocale[locale] || AppLocale.en;
  return (
    <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
      <CompleteProfileForm />
    </IntlProvider>
  );
};

const CompleteProfileForm: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const authUser = useSelector((state: AppState) => (state as any).globalReducer.authUser);
  const existingMobile: string | undefined = authUser?.user_info?.mobile;
  const existingMobileCountryCode = Object.keys(MOBILE_RULES).find((code) => existingMobile?.startsWith(code));
  const [countryCode, setCountryCode] = useState(existingMobileCountryCode || '+966');
  const language = useSelector((state: any) => state.LanguageSwitcher);
  const isArabic = language.language.locale === 'ar';
  const direction = isArabic ? 'rtl' : 'ltr';
  const mobileRule = MOBILE_RULES[countryCode] || MOBILE_RULES['+966'];
  const today = new Date().toISOString().slice(0, 10);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Re-validate the already-entered number against the newly selected country's format.
    if (form.getFieldValue('mobile')) {
      form.validateFields(['mobile']).catch(() => {});
    }
  }, [countryCode]);

  const onFinish = async (values: { mobile: string; dateOfBirth: string; gender: string }) => {
    setSubmitting(true);
    const result = await updateUserProfile(authUser.user_id, authUser.subject, {
      firstName: authUser.user_info?.first_name,
      lastName: authUser.user_info?.last_name,
      mobileNumber: countryCode + values.mobile,
      gender: values.gender,
      dateOfBirth: values.dateOfBirth,
    });
    setSubmitting(false);

    if (result) {
      notification.success({
        message: intl.formatMessage({
          id: 'patient.completeProfile.success.title',
          defaultMessage: 'Profile updated',
        }),
        description: intl.formatMessage({
          id: 'patient.completeProfile.success.description',
          defaultMessage: 'Thanks for completing your profile.',
        }),
        placement: isArabic ? 'topLeft' : 'topRight',
      });
      dispatch(
        updateAuthUserProps({
          authUser: {
            ...authUser,
            user_info: {
              ...authUser.user_info,
              mobile: countryCode + values.mobile,
              gender: values.gender,
              date_of_birth: values.dateOfBirth,
            },
          },
        })
      );
    }
  };

  return (
    <div className={styles.wrapper} dir={direction}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <img src={VctorsLogo} alt="Vctors Logo" />
        </div>
        <h1 className={styles.title}>
          {intl.formatMessage({ id: 'patient.completeProfile.title', defaultMessage: 'Complete Your Profile' })}
        </h1>
        <p className={styles.description}>
          {intl.formatMessage({
            id: 'patient.completeProfile.description',
            defaultMessage: 'Please fill in the missing details below to continue.',
          })}
        </p>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className={styles.form}
          initialValues={{
            mobile: existingMobileCountryCode ? existingMobile?.slice(existingMobileCountryCode.length) : undefined,
            dateOfBirth: authUser?.user_info?.date_of_birth || undefined,
            gender: authUser?.user_info?.gender || undefined,
          }}
        >
          <Form.Item
            label={intl.formatMessage({ id: 'patient.completeProfile.mobile.label', defaultMessage: 'Mobile Number' })}
            name="mobile"
            normalize={(value: string) => (value || '').replace(/\D/g, '')}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'patient.completeProfile.mobile.required',
                  defaultMessage: 'Please enter your mobile number',
                }),
              },
              {
                pattern: mobileRule.pattern,
                message: intl.formatMessage(
                  {
                    id: 'patient.completeProfile.mobile.invalid',
                    defaultMessage: 'Please enter a valid mobile number (e.g. {example})',
                  },
                  { example: mobileRule.example }
                ),
              },
            ]}
          >
            <Input
              addonBefore={
                <Select
                  value={countryCode}
                  onChange={(value) => setCountryCode(value as string)}
                  style={{ width: 90 }}
                >
                  <Option value="+20">+20</Option>
                  <Option value="+966">+966</Option>
                </Select>
              }
              inputMode="numeric"
              maxLength={mobileRule.length}
              placeholder={intl.formatMessage({
                id: 'patient.completeProfile.mobile.placeholder',
                defaultMessage: 'Enter mobile number',
              })}
            />
          </Form.Item>

          <Form.Item
            label={intl.formatMessage({
              id: 'patient.completeProfile.dateOfBirth.label',
              defaultMessage: 'Date Of Birth',
            })}
            name="dateOfBirth"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'patient.completeProfile.dateOfBirth.required',
                  defaultMessage: 'Please enter your date of birth',
                }),
              },
              {
                validator: (_, value) =>
                  !value || value <= today
                    ? Promise.resolve()
                    : Promise.reject(
                        intl.formatMessage({
                          id: 'patient.completeProfile.dateOfBirth.future',
                          defaultMessage: 'Date of birth cannot be in the future',
                        })
                      ),
              },
            ]}
          >
            <Input type="date" max={today} />
          </Form.Item>

          <Form.Item
            label={intl.formatMessage({ id: 'patient.completeProfile.gender.label', defaultMessage: 'Gender' })}
            name="gender"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'patient.completeProfile.gender.required',
                  defaultMessage: 'Please select your gender',
                }),
              },
            ]}
          >
            <Radio.Group>
              <Radio value="MALE">
                {intl.formatMessage({ id: 'patient.completeProfile.gender.male', defaultMessage: 'Male' })}
              </Radio>
              <Radio value="FEMALE">
                {intl.formatMessage({ id: 'patient.completeProfile.gender.female', defaultMessage: 'Female' })}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <div className={styles.actions}>
            <VButton vType="primary" variant="lg" htmlType="submit" loading={submitting}>
              {intl.formatMessage({ id: 'patient.completeProfile.submit', defaultMessage: 'Save & Continue' })}
            </VButton>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CompleteProfile;
